import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { useAuth } from "../context/AuthContext";
import { Sparkle, ArrowRight, Trash, Bookmark, ArrowLeft, DownloadSimple, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const TOOL_ROUTES = {
  bhrigu: "/bhrigu-bindu",
  chakras: "/chakras",
  nakshatra: "/nakshatra",
  compatibility: "/compatibility",
  rahu: "/rahu-placement",
  planet_effects: "/planet-effects",
  navamsa: "/navamsa",
  numerology_deep: "/numerology-deep",
  rudramsha: "/rudramsha",
  // `twenty_year` is deliberately ABSENT — that report has no React reading
  // page (the entire deliverable IS the PDF). The card hides the "Open
  // full reading" button for those rows and shows "View PDF" instead.
};

const TOOL_ACCENT = {
  bhrigu: { bg: "bg-amber-50", border: "border-amber-200", icon: "🎯" },
  chakras: { bg: "bg-rose-50", border: "border-rose-200", icon: "🪷" },
  nakshatra: { bg: "bg-indigo-50", border: "border-indigo-200", icon: "🌟" },
  compatibility: { bg: "bg-pink-50", border: "border-pink-200", icon: "💞" },
  rahu: { bg: "bg-violet-50", border: "border-violet-200", icon: "🚀" },
  planet_effects: { bg: "bg-orange-50", border: "border-orange-200", icon: "🪐" },
  navamsa: { bg: "bg-red-50", border: "border-red-200", icon: "💍" },
  numerology_deep: { bg: "bg-teal-50", border: "border-teal-200", icon: "🔢" },
  twenty_year: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "📔" },
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setReports([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/reports");
        setReports(data.reports || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load your reports.");
        setReports([]);
      }
    })();
  }, [user, authLoading]);

  const openReport = (r) => {
    const path = TOOL_ROUTES[r.tool];
    if (!path) return toast.error("Unknown tool.");
    navigate(`${path}?report=${r.report_id}`);
  };

  // Open the PDF inline in a new browser tab via the platform's native PDF
  // viewer. We deliberately fetch as a blob (with auth) and then open the
  // object-URL — this works regardless of whether frontend and backend share
  // an origin, and gives the user the "no download" reading experience they
  // expect on mobile + desktop.
  const viewPdf = async (r) => {
    if (busy) return;
    setBusy(true);
    const tid = toast.loading("Opening your PDF...");
    try {
      const res = await api.get(`/reports/${r.report_id}/pdf?inline=true`, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Newer browsers ignore window.open from non-trusted gestures; pre-allocate
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocked — fall back to same-tab navigation to the blob URL.
        // The browser still renders the PDF in its viewer.
        window.location.href = url;
      }
      // Don't revoke immediately — the new tab needs the blob URL alive.
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
      toast.success("Opened in new tab", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open PDF.", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async (r) => {
    if (busy) return;
    setBusy(true);
    const tid = toast.loading("Generating your PDF...");
    try {
      const res = await api.get(`/reports/${r.report_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeLabel = (r.tool_label || r.tool || "report").replace(/[^a-z0-9]+/gi, "_");
      a.download = `AstroKuber_${safeLabel}_${r.report_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
      toast.success("Downloaded!", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download PDF.", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const deleteReport = async (r) => {
    if (!window.confirm(`Delete this ${r.tool_label} report? You will lose your unlocked copy of this chart's insights.`)) return;
    setBusy(true);
    try {
      await api.delete(`/reports/${r.report_id}`);
      setReports((prev) => (prev || []).filter((x) => x.report_id !== r.report_id));
      toast.success("Report deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="reports-page">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="reports-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Bookmark size={14} weight="duotone" /> Your saved insights · free access forever
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Your <span className="italic text-gold">Reports</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          Every tool you've unlocked is saved here permanently. Open any report for the full reading — no need to re-pay. Use this as your personal astro-library across all your charts.
        </p>

        {!user && !authLoading && (
          <div className="mt-8 card-surface rounded-2xl p-6 text-center" data-testid="reports-signin">
            <p className="text-sm text-muted">Please sign in to view your saved reports.</p>
            <button
              onClick={() => {
                const url = window.location.origin + window.location.pathname;
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                startGoogleSignIn(url);
              }}
              className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium mt-4"
              data-testid="reports-signin-btn"
            >
              Sign in
            </button>
          </div>
        )}

        {user && reports === null && (
          <div className="mt-8 text-muted text-sm">Loading your library...</div>
        )}

        {user && reports?.length === 0 && (
          <div className="mt-8 card-surface rounded-2xl p-8 text-center" data-testid="reports-empty">
            <div className="text-6xl mb-3">📖</div>
            <h2 className="font-serif-display text-xl sm:text-2xl text-[#3F2D1B]">Your library is empty</h2>
            <p className="text-sm text-muted mt-2 max-w-md mx-auto">
              Unlock any premium calculator — the 20-Year Vedic Forecast, Bhrigu Bindu, Chakra heat-map, Nakshatra, Compatibility, Rahu, 9-Planet Effects, or D9 Navamsa — and your full report saves here automatically.
            </p>
            <Link to="/" className="inline-block btn-primary rounded-full px-6 py-2.5 text-sm font-medium mt-5">
              Explore calculators →
            </Link>
          </div>
        )}

        {user && reports && reports.length > 0 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="reports-list">
            {reports.map((r) => {
              const acc = TOOL_ACCENT[r.tool] || { bg: "bg-amber-50", border: "border-amber-200", icon: "✨" };
              const savedDate = r.saved_at ? new Date(r.saved_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
              const bd = r.birth_details || {};
              return (
                <div
                  key={r.report_id}
                  className={`rounded-2xl border ${acc.border} ${acc.bg} p-5 flex flex-col`}
                  data-testid={`report-card-${r.report_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-3xl">{acc.icon}</div>
                    <button
                      onClick={() => deleteReport(r)}
                      disabled={busy}
                      className="text-[#3F2D1B]/30 hover:text-rose-600 transition-colors p-1"
                      title="Delete report"
                      data-testid={`report-delete-${r.report_id}`}
                    >
                      <Trash size={14} weight="duotone" />
                    </button>
                  </div>
                  <h3 className="font-serif-display text-lg sm:text-xl text-[#3F2D1B] mt-2 leading-tight">
                    {r.tool_label || r.tool}
                  </h3>
                  <div className="mt-2 text-xs text-[#3F2D1B]/65 space-y-0.5">
                    {bd.date && (
                      <div>
                        <span className="opacity-60">Chart:</span> {bd.date} {bd.time && `· ${bd.time}`}
                      </div>
                    )}
                    {bd.place && (
                      <div className="truncate">
                        <span className="opacity-60">Place:</span> {bd.place}
                      </div>
                    )}
                    <div>
                      <span className="opacity-60">Saved:</span> {savedDate}
                    </div>
                  </div>
                  <button
                    onClick={() => openReport(r)}
                    className={`btn-primary rounded-full mt-4 py-2 text-xs font-medium flex items-center justify-center gap-1.5 ${TOOL_ROUTES[r.tool] ? '' : 'hidden'}`}
                    data-testid={`report-open-${r.report_id}`}
                  >
                    Open full reading <ArrowRight size={12} weight="bold" />
                  </button>
                  <button
                    onClick={() => viewPdf(r)}
                    disabled={busy}
                    className={`rounded-full ${TOOL_ROUTES[r.tool] ? 'mt-2 border border-amber-300 bg-white/60 hover:bg-white text-[#3F2D1B]' : 'mt-4 btn-primary'} py-2 text-xs font-medium flex items-center justify-center gap-1.5 transition disabled:opacity-60`}
                    data-testid={`report-view-${r.report_id}`}
                  >
                    <Eye size={12} weight="bold" /> View PDF in browser
                  </button>
                  <button
                    onClick={() => downloadPdf(r)}
                    disabled={busy}
                    className="rounded-full mt-2 py-2 text-xs font-medium flex items-center justify-center gap-1.5 border border-amber-300 bg-white/60 hover:bg-white text-[#3F2D1B] transition disabled:opacity-60"
                    data-testid={`report-pdf-${r.report_id}`}
                  >
                    <DownloadSimple size={12} weight="bold" /> Download PDF
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
